import { Grid, useTheme } from "@mui/material";
import { useRef, useState } from "react";

export const PinCode = ({
    onChange,
    quantity = 4
}: {
    onChange: (complete: boolean, value: string) => void,
    quantity?: number
}) => {
    const inputsRef = useRef<HTMLInputElement[]>([]);
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
    const { palette } = useTheme();

    const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos

        if (!value) return; // Evita avançar se estiver vazio

        event.target.value = value; // Garante que apenas números sejam inseridos

        if (index < inputsRef.current.length - 1) {
            inputsRef.current[index + 1]?.focus();
        }

        // Verifica se todos os inputs estão preenchidos
        const fullValue = inputsRef.current.map(input => input.value).join("");
        onChange(fullValue.length === quantity, fullValue);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        let pasteData = event.clipboardData.getData("text").replace(/\D/g, ""); // Mantém apenas números

        if (pasteData.length > quantity) {
            pasteData = pasteData.substring(0, quantity); // Limita a quantity caracteres
        }

        pasteData.split("").forEach((num, i) => {
            if (inputsRef.current[i]) {
                inputsRef.current[i].value = num;
            }
        });

        inputsRef.current[inputsRef.current.length - 1]?.focus();

        const fullValue = inputsRef.current.map(input => input.value).join("");
        onChange(fullValue.length === quantity, fullValue);

        event.preventDefault();
    };

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace" && !event.currentTarget.value) {
            if (index > 0) {
                inputsRef.current[index - 1]?.focus();
            }
        }
        // Atualiza o valor e chama onChange
        setTimeout(() => {
            const fullValue = inputsRef.current.map(input => input.value).join("");
            onChange(fullValue.length === quantity, fullValue);
        }, 10);
    };

    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
        (event.target as HTMLInputElement).value = (event.target as HTMLInputElement).value.replace(/\D/g, ""); // Evita inserir letras
    };

    const handleFocus = (index: number) => {
        setFocusedIndex(index);
    };

    return (
        <Grid display="flex" direction="row" gap="1rem">
            {Array(quantity).fill(null).map((_, index) => (
                <Grid
                    key={index}
                    width={60}
                    height={60}
                    container
                    alignItems="center"
                    justifyContent="center"
                    border={index === focusedIndex ? `2px solid${palette.primary.main} ` : `2px solid gray`}
                    borderRadius={4}
                    sx={{
                        transition: "all .1s ease-out",
                        ...(index !== focusedIndex && {
                            transform: "scale(.95)"
                        })
                    }}
                >
                    <input
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        pattern="\d*"
                        type="tel"
                        inputMode="numeric"
                        maxLength={1}
                        ref={(el) => (inputsRef.current[index] = el!)}
                        onChange={(e) => handleChange(index, e)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        onInput={handleInput}
                        onFocus={() => handleFocus(index)}
                        style={{
                            width: "100%",
                            height: "100%",
                            textAlign: "center",
                            fontSize: "3.5rem",
                            fontWeight: "600",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                        }}
                    />
                </Grid>
            ))}
        </Grid>
    );
};
